import React, { useContext, useEffect, useState, Suspense } from "react";
import { faAdd, faMinus, faPen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Switch from "react-switch";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Nav, NavItem, NavLink, Row, Spinner, TabContent, TabPane } from "reactstrap";
import { PostData } from "../../../../services/FetchData";
import { toast } from "react-toastify";
import { ProductContext } from "../../../../store";
import { Link } from "react-router-dom";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import { FacetItem } from "../../../../components/Search/FacetItem";
import { SortableItem } from "../../../../components/Search/SortableItem";
import conversionBoxInstantSearchScript from "../../../../components/Search/SearchOptions/Bigcommerce/conversionBoxInstantSearchScript.js";
import conversionBoxQuickPreviewScript from "../../../../components/Search/SearchOptions/Bigcommerce/ConversionBoxQuickPreview.js";
import IndexDropdown from "../../../../components/Drupal/IndexDropdown";
import FacetConfigList from "../../../../components/Drupal/FacetConfigList";
import SortConfigList from "../../../../components/Drupal/SortConfigList";

const DRUPAL_BASE = "ms3";

const JavaScriptCodeEditor = React.lazy(() => import("../../../../components/Search/JavaScriptCodeEditor"));

const DRUPAL_SORT_OPTIONS = [
	{ value: "text_match:desc", label: "Relevance" },
	{ value: "created:desc", label: "Newest first" },
	{ value: "created:asc", label: "Oldest first" },
	{ value: "price_number:asc", label: "Price: Low to High" },
	{ value: "price_number:desc", label: "Price: High to Low" },
	{ value: "title:asc", label: "Name: A to Z" },
];

// APIs: getInstantSearchConfig, saveInstantSearch, toggleInstantSearch (same path names for all platforms)
const PLATFORMS = { MAGENTO: "magento", SHOPIFY: "shopify", BIGCOMMERCE: "bigcommerce", WOOCOMMERCE: "woocommerce", DRUPAL: "drupal" };
const PLATFORM_PREFIX = { [PLATFORMS.MAGENTO]: "magento", [PLATFORMS.SHOPIFY]: "shopify", [PLATFORMS.BIGCOMMERCE]: "bigcommerce", [PLATFORMS.DRUPAL]: "drupal", [PLATFORMS.WOOCOMMERCE]: "woocommerce" };

const SHOPIFY_GRID_LAYOUTS = [
	{ value: "Auto", label: "Auto" },
	{ value: "1", label: "1" },
	{ value: "2", label: "2" },
	{ value: "3", label: "3" },
	{ value: "4", label: "4" },
	{ value: "5", label: "5" },
	{ value: "6", label: "6" },
];

const getApiConfig = (platform) => {
	const base = platform === PLATFORMS.MAGENTO ? "ms2" : "ms3";
	const prefix = PLATFORM_PREFIX[platform];
	const path = (endpoint) => `api/v1/${prefix}/${endpoint}`;
	return {
		base,
		getConfigPath: () => path("getInstantSearchConfig"),
		togglePath: () => path("toggleInstantSearch"),
		savePath: () => path("saveInstantSearch"),
	};
};

const InstantSearch = () => {
	const { currentWebsiteId, websiteData, websiteLoader } = useContext(ProductContext);
	const [loader, setLoader] = useState(true);
	const [saveLoader, setSaveLoader] = useState(false);
	const [toggleLoader, setToggleLoader] = useState(false);
	const [instantSearchConfig, setInstantSearchConfig] = useState(null);
	const [instantSortActiveFields, setInstantSortActiveFields] = useState([]);
	const [instantSortInactiveFields, setInstantSortInactiveFields] = useState([]);
	const [instantFacetActiveFields, setInstantFacetActiveFields] = useState([]);
	const [instantFacetInactiveFields, setInstantFacetInactiveFields] = useState([]);
	const [renameModalFlag, setRenameModalFlag] = useState(false);
	const [currentSortField, setCurrentSortField] = useState({});
	const [currentSortIndex, setCurrentSortIndex] = useState(null);
	const [editModalFlag, setEditModalFlag] = useState(false);
	const [currentFacetField, setCurrentFacetField] = useState({});
	const [currentFacetIndex, setCurrentFacetIndex] = useState(null);
	const [indexConfig, setIndexConfig] = useState({});
	const [editProductGridModal, setEditProductGridModal] = useState(false);
	const [productGridCode, setProductGridCode] = useState("");
	const [editQuickPreviewModal, setEditQuickPreviewModal] = useState(false);
	const [quickPreviewCode, setQuickPreviewCode] = useState("");
	const [saveProductGridLoader, setSaveProductGridLoader] = useState(false);
	const [saveQuickPreviewLoader, setSaveQuickPreviewLoader] = useState(false);
	const [resetProductGridModal, setResetProductGridModal] = useState(false);
	const [resetQuickPreviewModal, setResetQuickPreviewModal] = useState(false);
	// Drupal: Product / Content tabs and Typesense schema per tab
	const [drupalActiveTab, setDrupalActiveTab] = useState("product");
	const [productSchemaFields, setProductSchemaFields] = useState([]);
	const [productSchemaLoader, setProductSchemaLoader] = useState(false);
	const [contentSchemaFields, setContentSchemaFields] = useState([]);
	const [contentSchemaLoader, setContentSchemaLoader] = useState(false);

	const platform = websiteData?.[currentWebsiteId]?.websiteType;
	const isShopify = platform === PLATFORMS.SHOPIFY;
	const isBigcommerce = platform === PLATFORMS.BIGCOMMERCE;
	const isMagento = platform === PLATFORMS.MAGENTO;
	const isDrupal = platform === PLATFORMS.DRUPAL;
	const { base, getConfigPath, togglePath, savePath } = getApiConfig(platform || "");
	const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

	useEffect(() => {
		if (!currentWebsiteId || !platform) return;
		getInstantSearchConfig();
	}, [currentWebsiteId, platform]);

	// Drupal: schema field name helper; facet dropdown only shows fields with facet === true
	const schemaFieldToName = (f) => (typeof f === "string" ? f : (f && (f.name || f.field || f.field_name || f.attribute)));
	const schemaFieldsToNames = (arr) => (Array.isArray(arr) ? arr.map(schemaFieldToName).filter(Boolean) : []);
	const schemaFieldsToFacetNames = (arr) => (Array.isArray(arr)
		? arr.filter((f) => f && f.facet === true).map(schemaFieldToName).filter(Boolean)
		: []);

	// Drupal: load Typesense collection schema for product index (sort/facet options). Store raw fields; facet dropdown uses only facet: true.
	useEffect(() => {
		if (!isDrupal || !currentWebsiteId || !instantSearchConfig?.productSearchResultIndexId) {
			setProductSchemaFields([]);
			return;
		}
		setProductSchemaLoader(true);
		PostData(DRUPAL_BASE, "api/v1/drupal/collection-schema", {
			websiteId: currentWebsiteId,
			index_id: instantSearchConfig.productSearchResultIndexId,
			index_label: "Products",
		})
			.then((r) => {
				// Prefer full field objects (r.fields) so we have facet flag for facet dropdown
				const raw = r.fields || r.schema?.fields || r.data?.fields || r.collection?.fields || r.sortable_fields || [];
				setProductSchemaFields(Array.isArray(raw) ? raw : []);
			})
			.catch(() => setProductSchemaFields([]))
			.finally(() => setProductSchemaLoader(false));
	}, [isDrupal, currentWebsiteId, instantSearchConfig?.productSearchResultIndexId]);

	// Drupal: load Typesense collection schema for content index (sort/facet options). Store raw fields; facet dropdown uses only facet: true.
	useEffect(() => {
		if (!isDrupal || !currentWebsiteId || !instantSearchConfig?.searchResultIndexId) {
			setContentSchemaFields([]);
			return;
		}
		setContentSchemaLoader(true);
		PostData(DRUPAL_BASE, "api/v1/drupal/collection-schema", {
			websiteId: currentWebsiteId,
			index_id: instantSearchConfig.searchResultIndexId,
			index_label: "Content",
		})
			.then((r) => {
				// Prefer full field objects (r.fields) so we have facet flag for facet dropdown
				const raw = r.fields || r.schema?.fields || r.data?.fields || r.collection?.fields || r.sortable_fields || [];
				setContentSchemaFields(Array.isArray(raw) ? raw : []);
			})
			.catch(() => setContentSchemaFields([]))
			.finally(() => setContentSchemaLoader(false));
	}, [isDrupal, currentWebsiteId, instantSearchConfig?.searchResultIndexId]);

	const getInstantSearchConfig = () => {
		setLoader(true);
		if (platform === PLATFORMS.DRUPAL) {
			PostData(DRUPAL_BASE, "api/v1/drupal/getInstantSearchConfig", { websiteId: currentWebsiteId })
				.then((res) => {
					if (res?.status === "success" && res.data) {
						setInstantSearchConfig(res.data.instantSearchConfig || null);
						setIndexConfig(res.data.indexConfig || { indexIds: [] });
					} else {
						setInstantSearchConfig(null);
						setIndexConfig({ indexIds: [] });
					}
				})
				.catch((err) => {
					toast.error(err?.message || "Failed to load config");
					setInstantSearchConfig(null);
					setIndexConfig({ indexIds: [] });
				})
				.finally(() => setLoader(false));
			return;
		}
		PostData(base, getConfigPath(), { websiteId: currentWebsiteId }).then((result) => {
			if (result === "Invalid" || result === "undefined") {
				setLoader(false);
				return;
			}
			if (result.status === "success") {
				setInstantSearchConfig(result.data.instantSearchConfig);
				setInstantSortActiveFields(result.data.instantSortActiveFields || []);
				setInstantSortInactiveFields(result.data.instantSortInactiveFields || []);
				setInstantFacetActiveFields(result.data.instantFacetActiveFields || []);
				setInstantFacetInactiveFields(result.data.instantFacetInactiveFields || []);
				if (result.data.indexConfig) setIndexConfig(result.data.indexConfig || {});
				if (result.data.instantSearchConfig?.javascriptCode) setProductGridCode(result.data.instantSearchConfig.javascriptCode);
				if (result.data.instantSearchConfig?.quickPreviewCode) setQuickPreviewCode(result.data.instantSearchConfig.quickPreviewCode);
			} else {
				toast.error(result.message?.message || result.message);
			}
			setLoader(false);
		});
	};

	const toggleInstantSearch = () => {
		if (!instantSearchConfig) return;
		setToggleLoader(true);
		const newStatus = !instantSearchConfig.status;
		const onSuccess = () => {
			setInstantSearchConfig((c) => ({ ...c, status: !c.status }));
			toast.success("Instant Search status updated successfully");
			setToggleLoader(false);
		};
		const onFail = (result) => {
			toast.error(result?.message || "Failed to update");
			setToggleLoader(false);
		};
		if (platform === PLATFORMS.DRUPAL) {
			PostData(DRUPAL_BASE, "api/v1/drupal/toggleInstantSearch", { websiteId: currentWebsiteId, status: newStatus })
				.then((result) => {
					if (result?.status === "success" && result.data?.instantSearchConfig) {
						setInstantSearchConfig(result.data.instantSearchConfig);
					}
					onSuccess();
				})
				.catch((err) => {
					toast.error(err?.message || "Failed to update");
					setToggleLoader(false);
				});
			return;
		}
		const togglePayload = platform === PLATFORMS.MAGENTO
			? { websiteId: instantSearchConfig.websiteId, status: newStatus }
			: { websiteId: currentWebsiteId, status: instantSearchConfig.status };
		PostData(base, togglePath(), togglePayload).then((result) => {
			if (result?.status === "success") onSuccess();
			else onFail(result);
		});
	};

	const saveInstantSearchConfig = (onSuccess) => {
		if (!instantSearchConfig) return;
		if (!onSuccess) setSaveLoader(true);
		let payload;
		if (platform === PLATFORMS.MAGENTO) {
			payload = { ...instantSearchConfig, activeSort: instantSortActiveFields, inactiveSort: instantSortInactiveFields, activeFacet: instantFacetActiveFields, inactiveFacet: instantFacetInactiveFields };
		} else if (platform === PLATFORMS.SHOPIFY) {
			payload = {
				cssSelector: instantSearchConfig.cssSelector,
				noOfProducts: instantSearchConfig.noOfProducts,
				gridColumns: instantSearchConfig.gridColumns,
				showPrice: instantSearchConfig.showPrice,
				outOfStock: instantSearchConfig.outOfStock,
				titleMaxLines: instantSearchConfig.titleMaxLines,
				showDescription: instantSearchConfig.showDescription,
				descriptionMaxLines: instantSearchConfig.descriptionMaxLines,
				showSKU: instantSearchConfig.showSKU,
				flipImageHover: instantSearchConfig.flipImageHover,
				noOfPages: instantSearchConfig.noOfPages,
				noOfBlogs: instantSearchConfig.noOfBlogs,
				infiniteScroll: instantSearchConfig.infiniteScroll,
				showVendor: instantSearchConfig.showVendor,
				javascriptCode: productGridCode || instantSearchConfig.javascriptCode,
				activeSort: instantSortActiveFields,
				inactiveSort: instantSortInactiveFields,
				activeFacet: instantFacetActiveFields,
				inactiveFacet: instantFacetInactiveFields,
				websiteId: currentWebsiteId,
			};
		} else if (platform === PLATFORMS.BIGCOMMERCE) {
			payload = {
				...instantSearchConfig,
				websiteId: instantSearchConfig.websiteId,
				javascriptCode: productGridCode || instantSearchConfig.javascriptCode,
				quickPreviewCode: quickPreviewCode || instantSearchConfig.quickPreviewCode,
				activeSort: instantSortActiveFields,
				inactiveSort: instantSortInactiveFields,
				activeFacet: instantFacetActiveFields,
				inactiveFacet: instantFacetInactiveFields,
			};
		} else if (platform === PLATFORMS.DRUPAL) {
			PostData(DRUPAL_BASE, "api/v1/drupal/saveInstantSearch", {
				websiteId: currentWebsiteId,
				instantSearchConfig: {
					status: !!instantSearchConfig.status,
					searchResultIndexId: instantSearchConfig.searchResultIndexId || "",
					perPage: instantSearchConfig.perPage != null ? instantSearchConfig.perPage : 24,
					sort: instantSearchConfig.sort || "text_match:desc",
					sortOrders: instantSearchConfig.sortOrders || [],
					facets: instantSearchConfig.facets || [],
					productSearchResultIndexId: instantSearchConfig.productSearchResultIndexId || "",
					productPerPage: instantSearchConfig.productPerPage != null ? instantSearchConfig.productPerPage : 24,
					productSortOrders: instantSearchConfig.productSortOrders || [],
					productFacets: instantSearchConfig.productFacets || [],
					productInfiniteScroll: !!instantSearchConfig.productInfiniteScroll,
					productFlipImageHover: !!instantSearchConfig.productFlipImageHover,
					productGroupByField: !!instantSearchConfig.productGroupByField,
					productDisplay: instantSearchConfig.productDisplay || {},
					semanticSearchEnabled: !!instantSearchConfig.semanticSearchEnabled,
					semanticSourceFields: instantSearchConfig.semanticSourceFields || ["product_title", "body"],
					semanticHybridAlpha: instantSearchConfig.semanticHybridAlpha ?? 0.3,
					nlQueryEnabled: !!instantSearchConfig.nlQueryEnabled,
					nlModelId: instantSearchConfig.nlModelId || null,
				},
			})
				.then((result) => {
					if (result?.status === "success") {
						toast.success(result.message || "Saved");
						if (typeof onSuccess === "function") onSuccess();
					} else {
						toast.error(result?.message || "Failed to save");
					}
				})
				.catch((err) => {
					toast.error(err?.message || "Failed to save");
					if (typeof onSuccess === "function") onSuccess?.();
				})
				.finally(() => {
					if (typeof onSuccess !== "function") setSaveLoader(false);
				});
			return;
		} else {
			payload = { ...instantSearchConfig, websiteId: instantSearchConfig.websiteId, activeSort: instantSortActiveFields, inactiveSort: instantSortInactiveFields, activeFacet: instantFacetActiveFields, inactiveFacet: instantFacetInactiveFields };
		}
		PostData(base, savePath(), payload).then((result) => {
			if (result?.status === "success") {
				toast.success(result.message || "Saved");
				if (typeof onSuccess === "function") onSuccess();
			} else {
				toast.error(result?.message || "Failed to save");
				if (typeof onSuccess === "function") {
					setSaveProductGridLoader(false);
					setSaveQuickPreviewLoader(false);
				}
			}
			if (typeof onSuccess !== "function") setSaveLoader(false);
		});
	};

	const saveProductGridFromModal = () => {
		setSaveProductGridLoader(true);
		saveInstantSearchConfig(() => {
			setEditProductGridModal(false);
			setSaveProductGridLoader(false);
		});
	};

	const saveQuickPreviewFromModal = () => {
		setSaveQuickPreviewLoader(true);
		saveInstantSearchConfig(() => {
			setEditQuickPreviewModal(false);
			setSaveQuickPreviewLoader(false);
		});
	};

	const changeInstantSearchConfig = (key, value) => {
		setInstantSearchConfig((c) => (c ? { ...c, [key]: value } : null));
	};

	const findIndexByKey = (arr, key, val) => arr.findIndex((item) => item[key] === val);

	const handleSortDragEnd = (event) => {
		const { active, over } = event;
		if (!over || active.id?.sortFieldId === over.id?.sortFieldId) return;
		const oldIndex = findIndexByKey(instantSortActiveFields, "sortFieldId", active.id?.sortFieldId);
		const newIndex = findIndexByKey(instantSortActiveFields, "sortFieldId", over.id?.sortFieldId);
		if (oldIndex >= 0 && newIndex >= 0) setInstantSortActiveFields(arrayMove(instantSortActiveFields, oldIndex, newIndex));
	};

	const handleFacetDragEnd = (event) => {
		const { active, over } = event;
		if (!over || active.id?.facetFieldId === over.id?.facetFieldId) return;
		const oldIndex = findIndexByKey(instantFacetActiveFields, "facetFieldId", active.id?.facetFieldId);
		const newIndex = findIndexByKey(instantFacetActiveFields, "facetFieldId", over.id?.facetFieldId);
		if (oldIndex >= 0 && newIndex >= 0) setInstantFacetActiveFields(arrayMove(instantFacetActiveFields, oldIndex, newIndex));
	};

	const closeSortTitleModal = () => { setCurrentSortField({}); setCurrentSortIndex(null); setRenameModalFlag(false); };
	const closeEditFacetModal = () => { setCurrentFacetField({}); setCurrentFacetIndex(null); setEditModalFlag(false); };

	const saveSortTitle = () => {
		const next = [...instantSortActiveFields];
		if (currentSortIndex != null && next[currentSortIndex]) next[currentSortIndex] = { ...next[currentSortIndex], title: currentSortField.title };
		setInstantSortActiveFields(next);
		closeSortTitleModal();
	};

	const saveFacetValue = () => {
		const next = [...instantFacetActiveFields];
		if (currentFacetIndex != null && next[currentFacetIndex]) next[currentFacetIndex] = currentFacetField;
		setInstantFacetActiveFields(next);
		closeEditFacetModal();
	};

	const renameModalTrigger = (index) => { setCurrentSortIndex(index); setCurrentSortField(instantSortActiveFields[index] || {}); setRenameModalFlag(true); };
	const editFacetTrigger = (index) => { setCurrentFacetIndex(index); setCurrentFacetField(instantFacetActiveFields[index] || {}); setEditModalFlag(true); };

	const handleSortTitle = (value) => setCurrentSortField((f) => ({ ...f, title: value }));
	const handleFacetTitle = (value) => setCurrentFacetField((f) => ({ ...f, title: value }));
	const searchableChange = (value) => setCurrentFacetField((f) => ({ ...f, searchable: value }));
	const facetTypeChange = (value) => setCurrentFacetField((f) => ({ ...f, facetType: value }));

	const activeSortField = (index) => {
		const inactive = [...instantSortInactiveFields];
		const newActive = inactive[index];
		inactive.splice(index, 1);
		const active = [...instantSortActiveFields];
		if (newActive) { newActive.order = active.length; active.push(newActive); }
		setInstantSortActiveFields(active);
		setInstantSortInactiveFields(inactive);
	};

	const activatedFacetField = (index) => {
		const inactive = [...instantFacetInactiveFields];
		const newActive = inactive[index];
		inactive.splice(index, 1);
		const active = [...instantFacetActiveFields];
		if (newActive) { newActive.order = active.length; active.push(newActive); }
		setInstantFacetActiveFields(active);
		setInstantFacetInactiveFields(inactive);
	};

	const deactiveSortField = (index) => {
		const inactive = [...instantSortInactiveFields];
		const active = [...instantSortActiveFields];
		const removed = active[index];
		active.splice(index, 1);
		if (removed) inactive.push(removed);
		setInstantSortActiveFields(active);
		setInstantSortInactiveFields(inactive);
	};

	const deactiveFacetField = (index) => {
		const inactive = [...instantFacetInactiveFields];
		const active = [...instantFacetActiveFields];
		const removed = active[index];
		active.splice(index, 1);
		if (removed) inactive.push(removed);
		setInstantFacetActiveFields(active);
		setInstantFacetInactiveFields(inactive);
	};

	const priceOptions = [{ label: "Slider", value: "slider" }];
	const otherOptions = [{ label: "Conjunctive", value: "conjunctive" }, { label: "Disjunctive", value: "disjunctive" }];

	if (websiteLoader || !websiteData?.[currentWebsiteId]) return <DetailSkeleton />;
	if (!platform || !["magento", "shopify", "bigcommerce", "woocommerce", "drupal"].includes(platform)) return <DetailSkeleton />;
	if (loader || !instantSearchConfig) return <DetailSkeleton />;

	return (
		<>
			<div className="search-options-card">
				<div className="card-header">
					<div className="title-text">
						<h3>{isDrupal ? "Instant Search - Search result page" : "InstantSearch - Results page powered by ConversionBox"}</h3>
						<span>{isDrupal ? "Configure the search result page (index, results per page, sort, facet filters)." : "Replace the default search page with ConversionBox&apos;s InstantSearch, to provide your users with instantaneous results, onscreen filtering (facets), and other enhanced search features."}</span>
					</div>
					<div className="switch-container">
						<span>{instantSearchConfig.status ? "Enabled" : "Disabled"}</span>
						{toggleLoader ? <Spinner /> : (
							<Switch checked={!!instantSearchConfig.status} onChange={toggleInstantSearch} onColor="#9eb3fc" onHandleColor="#3c64f4" handleDiameter={25} uncheckedIcon={false} checkedIcon={false} boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)" activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)" height={15} width={40} className="react-switch" id="material-switch" />
						)}
					</div>
				</div>
				<div className="card-content">
					<form>
						{isDrupal && (
							<>
								<Row className="mb-3">
									<Col xs={12}>
										<p className="text-muted small mb-0">
											Configure search fields and weights for autocomplete and this page:{" "}
											<Link to="/search/searchOptions/searchQueryFields">Search fields &amp; weights</Link>.
											{" "}Semantic &amp; NL search:{" "}
											<Link to="/search/searchOptions/semanticSearch">Semantic Search</Link>.
										</p>
									</Col>
								</Row>
								<div className="drupal-instant-tabs mb-4" style={{ borderBottom: "2px solid #dee2e6" }}>
									<Nav tabs className="nav-tabs mb-0" style={{ borderBottom: "none" }}>
										<NavItem>
											<NavLink
												className={drupalActiveTab === "product" ? "active" : ""}
												style={{ cursor: "pointer", border: "1px solid #dee2e6", borderBottom: drupalActiveTab === "product" ? "1px solid #fff" : "1px solid #dee2e6", marginBottom: drupalActiveTab === "product" ? "-2px" : "-1px", padding: "0.5rem 1rem", background: drupalActiveTab === "product" ? "#fff" : "#f8f9fa", borderRadius: "4px 4px 0 0" }}
												onClick={() => setDrupalActiveTab("product")}
											>
												Product
											</NavLink>
										</NavItem>
										<NavItem>
											<NavLink
												className={drupalActiveTab === "content" ? "active" : ""}
												style={{ cursor: "pointer", border: "1px solid #dee2e6", borderBottom: drupalActiveTab === "content" ? "1px solid #fff" : "1px solid #dee2e6", marginBottom: drupalActiveTab === "content" ? "-2px" : "-1px", padding: "0.5rem 1rem", background: drupalActiveTab === "content" ? "#fff" : "#f8f9fa", borderRadius: "4px 4px 0 0" }}
												onClick={() => setDrupalActiveTab("content")}
											>
												Content
											</NavLink>
										</NavItem>
									</Nav>
								</div>
								<TabContent activeTab={drupalActiveTab}>
									<TabPane tabId="product">
										<div className="mt-3">
											<Row className="mb-4 align-items-start">
												<Col xs={12} md={4}><label><h4>Product search result index</h4></label></Col>
												<Col xs={12} md={6}>
													<IndexDropdown
														indexIds={indexConfig.indexIds || []}
														value={instantSearchConfig.productSearchResultIndexId || ""}
														onChange={(val) => changeInstantSearchConfig("productSearchResultIndexId", val)}
														label=""
														placeholder="Select index for product results..."
													/>
													<div className="buttonContext"><p className="text-muted mb-0 small">Product search index. Sort and facet options are loaded from this index.</p></div>
												</Col>
											</Row>
											<Row className="mb-4 align-items-center">
												<Col xs={12} md={4}><label><h4>Results per page</h4></label></Col>
												<Col xs={12} md={6}>
													<div className="input-container">
														<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("productPerPage", Math.max(6, (instantSearchConfig.productPerPage != null ? instantSearchConfig.productPerPage : 24) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
														<Input type="number" min={6} max={100} value={instantSearchConfig.productPerPage != null ? instantSearchConfig.productPerPage : 24} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("productPerPage", Math.max(6, Math.min(100, v))); }} />
														<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("productPerPage", Math.min(100, (instantSearchConfig.productPerPage != null ? instantSearchConfig.productPerPage : 24) + 1))}><FontAwesomeIcon icon={faPlus} /></Button>
													</div>
												</Col>
											</Row>
											<Row className="mb-4 align-items-center">
												<Col xs={12} md={4}><label><h4>Infinite scroll</h4></label></Col>
												<Col xs={12} md={6}>
													<div className="checkboxContainer">
														<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.productInfiniteScroll} onChange={() => changeInstantSearchConfig("productInfiniteScroll", !instantSearchConfig.productInfiniteScroll)} />
													</div>
													<div className="buttonContext"><p className="text-muted mb-0 small">Load more results on scroll instead of pagination.</p></div>
												</Col>
											</Row>
											<Row className="mb-4 align-items-center">
												<Col xs={12} md={4}><label><h4>Flip image on hover</h4></label></Col>
												<Col xs={12} md={6}>
													<div className="checkboxContainer">
														<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.productFlipImageHover} onChange={() => changeInstantSearchConfig("productFlipImageHover", !instantSearchConfig.productFlipImageHover)} />
													</div>
												</Col>
											</Row>
											<Row className="mb-4 align-items-center">
												<Col xs={12} md={4}><label><h4>Group by product ID</h4></label></Col>
												<Col xs={12} md={6}>
													<div className="checkboxContainer">
														<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.productGroupByField} onChange={() => changeInstantSearchConfig("productGroupByField", !instantSearchConfig.productGroupByField)} />
													</div>
													<div className="buttonContext"><p className="text-muted mb-0 small">Deduplicate by product_id so one result per product (e.g. one per base product when you have variations).</p></div>
												</Col>
											</Row>
											{(() => {
												const d = instantSearchConfig.productDisplay || {};
												const setProductDisplay = (key, value) => changeInstantSearchConfig("productDisplay", { ...d, [key]: value });
												const titleMax = d.titleMaxLines != null ? d.titleMaxLines : 2;
												const descMax = d.descriptionMaxLines != null ? d.descriptionMaxLines : 2;
												return (
													<>
														<Row className="mb-4 align-items-center">
															<Col xs={12} md={4}><label><h4>Show price</h4></label></Col>
															<Col xs={12} md={6}>
																<div className="checkboxContainer">
																	<Input type="checkbox" className="checkboxContainerInput" checked={d.showPrice !== false} onChange={() => setProductDisplay("showPrice", !d.showPrice)} />
																</div>
															</Col>
														</Row>
														<Row className="mb-4 align-items-center">
															<Col xs={12} md={4}><label><h4>Show out of stock products</h4></label></Col>
															<Col xs={12} md={6}>
																<div className="checkboxContainer">
																	<Input type="checkbox" className="checkboxContainerInput" checked={!!d.outOfStock} onChange={() => setProductDisplay("outOfStock", !d.outOfStock)} />
																</div>
															</Col>
														</Row>
														<Row className="mb-4 align-items-center">
															<Col xs={12} md={4}><label><h4>Maximum title lines</h4></label></Col>
															<Col xs={12} md={6}>
																<div className="input-container">
																	<Button type="button" className="btn-minus" onClick={() => setProductDisplay("titleMaxLines", Math.max(1, titleMax - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
																	<Input type="number" min={1} max={5} value={titleMax} onChange={(e) => setProductDisplay("titleMaxLines", Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 2)))} />
																	<Button type="button" className="btn-plus" onClick={() => setProductDisplay("titleMaxLines", Math.min(5, titleMax + 1))}><FontAwesomeIcon icon={faPlus} /></Button>
																</div>
																<div className="buttonContext"><p className="text-muted mb-0 small">Max lines for product title in results.</p></div>
															</Col>
														</Row>
														<Row className="mb-4 align-items-center">
															<Col xs={12} md={4}><label><h4>Show product description</h4></label></Col>
															<Col xs={12} md={6}>
																<div className="checkboxContainer">
																	<Input type="checkbox" className="checkboxContainerInput" checked={!!d.showDescription} onChange={() => setProductDisplay("showDescription", !d.showDescription)} />
																</div>
															</Col>
														</Row>
														{d.showDescription && (
															<Row className="mb-4 align-items-center">
																<Col xs={12} md={4}><label><h4>Maximum description lines</h4></label></Col>
																<Col xs={12} md={6}>
																	<div className="input-container">
																		<Button type="button" className="btn-minus" onClick={() => setProductDisplay("descriptionMaxLines", Math.max(1, descMax - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
																		<Input type="number" min={1} max={10} value={descMax} onChange={(e) => setProductDisplay("descriptionMaxLines", Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 2)))} />
																		<Button type="button" className="btn-plus" onClick={() => setProductDisplay("descriptionMaxLines", Math.min(10, descMax + 1))}><FontAwesomeIcon icon={faPlus} /></Button>
																	</div>
																</Col>
															</Row>
														)}
														<Row className="mb-4 align-items-center">
															<Col xs={12} md={4}><label><h4>Show vendor / brand</h4></label></Col>
															<Col xs={12} md={6}>
																<div className="checkboxContainer">
																	<Input type="checkbox" className="checkboxContainerInput" checked={!!d.showVendor} onChange={() => setProductDisplay("showVendor", !d.showVendor)} />
																</div>
															</Col>
														</Row>
													</>
												);
											})()}
											<Row className="mb-4 mt-4">
												<Col xs={12} md={4}><label><h4>Sort orders</h4></label></Col>
												<Col xs={12} md={8}>
													<p className="text-muted small mb-2">Default sort is <strong>Relevance</strong>. Define sort options by choosing a <strong>field from the collection schema</strong> (loaded from the product index above), direction, and display label.</p>
													{productSchemaLoader && <p className="text-muted small">Loading sort fields from collection schema...</p>}
													{!productSchemaLoader && !(productSchemaFields && productSchemaFields.length) && instantSearchConfig.productSearchResultIndexId && <p className="text-muted small mb-2">No schema fields returned for this index.</p>}
													{!productSchemaLoader && !instantSearchConfig.productSearchResultIndexId && <p className="text-muted small mb-2">Select a product index above to load sort fields from the collection schema.</p>}
													<SortConfigList
														sortOrders={instantSearchConfig.productSortOrders || []}
														onChange={(next) => changeInstantSearchConfig("productSortOrders", next)}
														fieldOptions={schemaFieldsToNames(productSchemaFields)}
														schemaLoading={productSchemaLoader}
													/>
												</Col>
											</Row>
											<Row className="mb-4">
												<Col xs={12} md={4}><label><h4>Facet filters</h4></label></Col>
												<Col xs={12} md={8}>
													<p className="text-muted small mb-2">Define which collection attributes appear as filters. Choose an attribute from the collection schema and set the display label. Use order to control the display order.</p>
													{productSchemaLoader && <p className="text-muted small">Loading facet options from collection...</p>}
													<FacetConfigList
														facets={instantSearchConfig.productFacets || []}
														onChange={(next) => changeInstantSearchConfig("productFacets", next)}
														fieldOptions={schemaFieldsToFacetNames(productSchemaFields)}
													/>
												</Col>
											</Row>
										</div>
									</TabPane>
									<TabPane tabId="content">
										<div className="mt-3">
											<Row className="mb-4 align-items-start">
												<Col xs={12} md={4}><label><h4>Search result index (content)</h4></label></Col>
												<Col xs={12} md={6}>
													<IndexDropdown
														indexIds={indexConfig.indexIds || []}
														value={instantSearchConfig.searchResultIndexId || ""}
														onChange={(val) => changeInstantSearchConfig("searchResultIndexId", val)}
														label=""
														placeholder="Select index for result page..."
													/>
												</Col>
											</Row>
											<Row className="mb-4 align-items-center">
												<Col xs={12} md={4}><label><h4>Results per page</h4></label></Col>
												<Col xs={12} md={6}>
													<div className="input-container">
														<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("perPage", Math.max(6, (instantSearchConfig.perPage != null ? instantSearchConfig.perPage : 24) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
														<Input type="number" min={6} max={100} value={instantSearchConfig.perPage != null ? instantSearchConfig.perPage : 24} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("perPage", Math.max(6, Math.min(100, v))); }} />
														<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("perPage", Math.min(100, (instantSearchConfig.perPage != null ? instantSearchConfig.perPage : 24) + 1))}><FontAwesomeIcon icon={faPlus} /></Button>
													</div>
												</Col>
											</Row>
											<Row className="mb-4 mt-4">
												<Col xs={12} md={4}><label><h4>Sort orders</h4></label></Col>
												<Col xs={12} md={8}>
													<p className="text-muted small mb-2">Default sort is <strong>Relevance</strong>. Define sort options by choosing a <strong>field from the collection schema</strong> (loaded from the content index above), direction, and display label.</p>
													{contentSchemaLoader && <p className="text-muted small">Loading sort fields from collection schema...</p>}
													{!contentSchemaLoader && !(contentSchemaFields && contentSchemaFields.length) && instantSearchConfig.searchResultIndexId && <p className="text-muted small mb-2">No schema fields returned for this index.</p>}
													{!contentSchemaLoader && !instantSearchConfig.searchResultIndexId && <p className="text-muted small mb-2">Select a content index above to load sort fields from the collection schema.</p>}
													<SortConfigList
														sortOrders={instantSearchConfig.sortOrders || []}
														onChange={(next) => changeInstantSearchConfig("sortOrders", next)}
														fieldOptions={schemaFieldsToNames(contentSchemaFields)}
														schemaLoading={contentSchemaLoader}
													/>
												</Col>
											</Row>
											<Row className="mb-4">
												<Col xs={12} md={4}><label><h4>Facet filters</h4></label></Col>
												<Col xs={12} md={8}>
													<p className="text-muted small mb-2">Define which collection attributes appear as filters. Choose an attribute from the collection schema and set the display label. Use order to control the display order.</p>
													{contentSchemaLoader && <p className="text-muted small">Loading facet options from collection...</p>}
													<FacetConfigList
														facets={instantSearchConfig.facets || []}
														onChange={(next) => changeInstantSearchConfig("facets", next)}
														fieldOptions={schemaFieldsToFacetNames(contentSchemaFields)}
													/>
												</Col>
											</Row>
										</div>
									</TabPane>
								</TabContent>
								<Row className="justify-content-end mt-4">
									<Col xs="auto">
										<Button type="button" color="primary" onClick={() => saveInstantSearchConfig()} disabled={saveLoader}>
											{saveLoader ? <Spinner size="sm" /> : "Save"}
										</Button>
									</Col>
								</Row>
							</>
						)}
						{!isDrupal && isShopify && (
							<Row className="mb-4">
								<Col xs={12}><h4>Customize instant search settings</h4></Col>
							</Row>
						)}
						{isShopify && (
							<>
								<Row className="mb-4 align-items-start">
									<Col xs={12} md={4}><label><h4>CSS Selector</h4></label></Col>
									<Col xs={12} md={6}>
										<Input type="text" placeholder="Enter CSS selector" value={instantSearchConfig.cssSelector || ""} onChange={(e) => changeInstantSearchConfig("cssSelector", e.target.value)} />
										<div className="buttonContext"><p className="text-muted mb-0">CSS Selector for the search input. You might have to change this value to an element selector that works for your theme.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>No Of Products Shown</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("noOfProducts", Math.max(1, (instantSearchConfig.noOfProducts || 12) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.noOfProducts} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("noOfProducts", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("noOfProducts", (instantSearchConfig.noOfProducts || 12) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Product grid column</h4></label></Col>
									<Col xs={12} md={6}>
										<Input type="select" value={instantSearchConfig.gridColumns ?? "Auto"} onChange={(e) => changeInstantSearchConfig("gridColumns", e.target.value)}>
											{SHOPIFY_GRID_LAYOUTS.map((layout) => (
												<option key={layout.value} value={layout.value}>{layout.label}</option>
											))}
										</Input>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Price</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showPrice} onChange={() => changeInstantSearchConfig("showPrice", !instantSearchConfig.showPrice)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Out Of Stock Products</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.outOfStock} onChange={() => changeInstantSearchConfig("outOfStock", !instantSearchConfig.outOfStock)} />
										</div>
										<div className="buttonContext"><p className="mb-0">Products marked as not in stock will show up in search results.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Maximum Title Lines</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("titleMaxLines", Math.max(1, (instantSearchConfig.titleMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.titleMaxLines} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("titleMaxLines", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("titleMaxLines", (instantSearchConfig.titleMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Product Description</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showDescription} onChange={() => changeInstantSearchConfig("showDescription", !instantSearchConfig.showDescription)} />
										</div>
									</Col>
								</Row>
								{instantSearchConfig.showDescription && (
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Maximum Description Lines</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("descriptionMaxLines", Math.max(1, (instantSearchConfig.descriptionMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={instantSearchConfig.descriptionMaxLines} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("descriptionMaxLines", v); }} />
												<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("descriptionMaxLines", (instantSearchConfig.descriptionMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
										</Col>
									</Row>
								)}
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show SKU</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showSKU} onChange={() => changeInstantSearchConfig("showSKU", !instantSearchConfig.showSKU)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Flip Image On Hover</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.flipImageHover} onChange={() => changeInstantSearchConfig("flipImageHover", !instantSearchConfig.flipImageHover)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Infinite/Endless Scrolling</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.infiniteScroll} onChange={() => changeInstantSearchConfig("infiniteScroll", !instantSearchConfig.infiniteScroll)} />
										</div>
										<div className="buttonContext"><p className="mb-0">Products will be displayed not by pages, but will be loaded on scroll</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Vendor</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showVendor} onChange={() => changeInstantSearchConfig("showVendor", !instantSearchConfig.showVendor)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4">
									<Col xs={12} md={4}><label><h4>Sort Orders</h4></label></Col>
									<Col xs={12} md={8}>
										<div style={{ display: "flex", flexDirection: "column" }}>
											<div className="buttonContext mb-2">
												<p className="mb-0">Enable and configure the different product sorting options. In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
											</div>
											<div><b>Active</b></div>
											<div className="buttonContext mb-2">Use drag and drop to customise the order.</div>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
												<SortableContext items={instantSortActiveFields} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{instantSortActiveFields.map((id, index) => (
															<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTrigger} onDeactivateBtnClicked={deactiveSortField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
											<h4>Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{instantSortInactiveFields.length > 0 ? (
													<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
														{instantSortInactiveFields.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activeSortField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive sort fields</span>
												)}
											</div>
										</div>
									</Col>
								</Row>
								<Row className="mb-4">
									<Col xs={12} md={4}><label><h4>Facets</h4></label></Col>
									<Col xs={12} md={8}>
										<div style={{ display: "flex", flexDirection: "column" }}>
											<p>Allow customers to explore the catalog by applying multiple filters.</p>
											<div><b>Active</b></div>
											<div className="buttonContext mb-2">Use drag and drop to customise the order.</div>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFacetDragEnd}>
												<SortableContext items={instantFacetActiveFields} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{instantFacetActiveFields.map((id, index) => (
															<FacetItem key={index} index={index} id={id} onEditClick={editFacetTrigger} onDeactivateBtnClicked={deactiveFacetField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
											<h4>Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{instantFacetInactiveFields.length > 0 ? (
													<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
														{instantFacetInactiveFields.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activatedFacetField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive facet fields</span>
												)}
											</div>
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-start">
									<Col xs={12} md={4}><label><h4>Edit Product Grid</h4></label></Col>
									<Col xs={12} md={6}>
										<Button color="outline-primary" style={{ minWidth: 80 }} onClick={() => { setProductGridCode(instantSearchConfig.javascriptCode || productGridCode || conversionBoxInstantSearchScript); setEditProductGridModal(true); }}>
											<FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />Edit
										</Button>
										<div className="buttonContext"><p className="mb-0">Add or edit the JavaScript used for the Instantsearch widget to customize the display of the product grid/list content.</p></div>
									</Col>
								</Row>
							</>
						)}
						{isBigcommerce && (
							<>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>No Of Products Shown</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("noOfProducts", Math.max(1, (instantSearchConfig.noOfProducts || 20) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.noOfProducts} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("noOfProducts", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("noOfProducts", (instantSearchConfig.noOfProducts || 20) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
										<div className="buttonContext"><p className="mb-0">Set number of products shown per search. Defaults to 20.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Product Grid Column</h4></label></Col>
									<Col xs={12} md={6}>
										<Input type="select" value={instantSearchConfig.gridColumns ?? "Auto"} onChange={(e) => changeInstantSearchConfig("gridColumns", e.target.value)}>
											{SHOPIFY_GRID_LAYOUTS.map((layout) => (
												<option key={layout.value} value={layout.value}>{layout.label}</option>
											))}
										</Input>
										<div className="buttonContext"><p className="mb-0">Products per row for grid view on desktop</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Out Of Stock Products</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.outOfStock} onChange={() => changeInstantSearchConfig("outOfStock", !instantSearchConfig.outOfStock)} />
										</div>
										<div className="buttonContext"><p className="mb-0">Products marked as not in stock will show up in search results.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Vendor</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showVendor} onChange={() => changeInstantSearchConfig("showVendor", !instantSearchConfig.showVendor)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Price</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showPrice} onChange={() => changeInstantSearchConfig("showPrice", !instantSearchConfig.showPrice)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Maximum Title Lines</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("titleMaxLines", Math.max(1, (instantSearchConfig.titleMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.titleMaxLines} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("titleMaxLines", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("titleMaxLines", (instantSearchConfig.titleMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Product Description</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showDescription} onChange={() => changeInstantSearchConfig("showDescription", !instantSearchConfig.showDescription)} />
										</div>
									</Col>
								</Row>
								{instantSearchConfig.showDescription && (
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Maximum Description Lines</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("descriptionMaxLines", Math.max(1, (instantSearchConfig.descriptionMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={instantSearchConfig.descriptionMaxLines} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("descriptionMaxLines", v); }} />
												<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("descriptionMaxLines", (instantSearchConfig.descriptionMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
										</Col>
									</Row>
								)}
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Flip Image On Hover</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.flipImageHover} onChange={() => changeInstantSearchConfig("flipImageHover", !instantSearchConfig.flipImageHover)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Infinite/Endless Scrolling</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.infiniteScroll} onChange={() => changeInstantSearchConfig("infiniteScroll", !instantSearchConfig.infiniteScroll)} />
										</div>
										<div className="buttonContext"><p className="mb-0">Products will be displayed not by pages, but will be loaded on scroll</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>No Of Pages Shown</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("noOfPages", Math.max(1, (instantSearchConfig.noOfPages || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.noOfPages} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("noOfPages", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("noOfPages", (instantSearchConfig.noOfPages || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
										<div className="buttonContext"><p className="mb-0">Number of pages to show in search. Defaults to 2.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>No Of Blogs Shown</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("noOfBlogs", Math.max(1, (instantSearchConfig.noOfBlogs || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.noOfBlogs} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("noOfBlogs", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("noOfBlogs", (instantSearchConfig.noOfBlogs || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
										<div className="buttonContext"><p className="mb-0">Number of blogs to show in search. Defaults to 2.</p></div>
									</Col>
								</Row>
								<Row className="mb-4">
									<Col xs={12} md={4}><label><h4>Sort Orders</h4></label></Col>
									<Col xs={12} md={8}>
										<div style={{ display: "flex", flexDirection: "column" }}>
											<div className="buttonContext mb-2">
												<p className="mb-0">Enable and configure the different product sorting options. In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
											</div>
											<div><b>Active</b></div>
											<div className="buttonContext mb-2">Use drag and drop to customise the order.</div>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
												<SortableContext items={instantSortActiveFields} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{instantSortActiveFields.map((id, index) => (
															<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTrigger} onDeactivateBtnClicked={deactiveSortField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
											<h4>Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{instantSortInactiveFields.length > 0 ? (
													<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
														{instantSortInactiveFields.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activeSortField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive sort fields</span>
												)}
											</div>
										</div>
									</Col>
								</Row>
								<Row className="mb-4">
									<Col xs={12} md={4}><label><h4>Facets</h4></label></Col>
									<Col xs={12} md={8}>
										<div style={{ display: "flex", flexDirection: "column" }}>
											<p>Allow customers to explore the catalog by applying multiple filters.</p>
											<div><b>Active</b></div>
											<div className="buttonContext mb-2">Use drag and drop to customise the order.</div>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFacetDragEnd}>
												<SortableContext items={instantFacetActiveFields} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{instantFacetActiveFields.map((id, index) => (
															<FacetItem key={index} index={index} id={id} onEditClick={editFacetTrigger} onDeactivateBtnClicked={deactiveFacetField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
											<h4>Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{instantFacetInactiveFields.length > 0 ? (
													<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
														{instantFacetInactiveFields.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activatedFacetField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive facet fields</span>
												)}
											</div>
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-start">
									<Col xs={12} md={4}><label><h4>Edit Product Grid</h4></label></Col>
									<Col xs={12} md={6}>
										<Button color="outline-primary" style={{ minWidth: 80 }} onClick={() => { setProductGridCode(instantSearchConfig.javascriptCode || productGridCode || conversionBoxInstantSearchScript); setEditProductGridModal(true); }}>
											<FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />Edit
										</Button>
										<div className="buttonContext"><p className="mb-0">Add or edit the JavaScript used for the Instantsearch widget to customize the display of the product grid/list content.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-start">
									<Col xs={12} md={4}><label><h4>Edit Quick Preview</h4></label></Col>
									<Col xs={12} md={6}>
										<Button color="outline-primary" style={{ minWidth: 80 }} onClick={() => { setQuickPreviewCode(instantSearchConfig.quickPreviewCode || quickPreviewCode || conversionBoxQuickPreviewScript); setEditQuickPreviewModal(true); }}>
											<FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />Edit
										</Button>
										<div className="buttonContext"><p className="mb-0">Add or edit the JavaScript used for the product quick preview content.</p></div>
									</Col>
								</Row>
							</>
						)}
						{!isShopify && !isBigcommerce && !isDrupal && (
							<>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>No Of Products Shown</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("noOfProducts", Math.max(1, (instantSearchConfig.noOfProducts || 12) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.noOfProducts} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("noOfProducts", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("noOfProducts", (instantSearchConfig.noOfProducts || 12) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
										<div className="buttonContext"><p className="mb-0">Number of products to show in search. Defaults to 12.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Out Of Stock Products</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.outOfStock} onChange={() => changeInstantSearchConfig("outOfStock", !instantSearchConfig.outOfStock)} />
										</div>
										<div className="buttonContext"><p className="mb-0">Products marked as not in stock will show up in search results.</p></div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Price</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showPrice} onChange={() => changeInstantSearchConfig("showPrice", !instantSearchConfig.showPrice)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show SKU</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showSKU} onChange={() => changeInstantSearchConfig("showSKU", !instantSearchConfig.showSKU)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Maximum Title Lines</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="input-container">
											<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("titleMaxLines", Math.max(1, (instantSearchConfig.titleMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
											<Input type="number" value={instantSearchConfig.titleMaxLines} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("titleMaxLines", v); }} />
											<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("titleMaxLines", (instantSearchConfig.titleMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Show Product Description</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.showDescription} onChange={() => changeInstantSearchConfig("showDescription", !instantSearchConfig.showDescription)} />
										</div>
									</Col>
								</Row>
								{instantSearchConfig.showDescription && (
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Maximum Description Lines</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => changeInstantSearchConfig("descriptionMaxLines", Math.max(1, (instantSearchConfig.descriptionMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={instantSearchConfig.descriptionMaxLines} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) changeInstantSearchConfig("descriptionMaxLines", v); }} />
												<Button type="button" className="btn-plus" onClick={() => changeInstantSearchConfig("descriptionMaxLines", (instantSearchConfig.descriptionMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
										</Col>
									</Row>
								)}
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Flip Image On Hover</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.flipImageHover} onChange={() => changeInstantSearchConfig("flipImageHover", !instantSearchConfig.flipImageHover)} />
										</div>
									</Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Enable Add to Cart</h4></label></Col>
									<Col xs={12} md={6}>
										<div className="checkboxContainer">
											<Input type="checkbox" className="checkboxContainerInput" checked={!!instantSearchConfig.enableAddToCart} onChange={() => changeInstantSearchConfig("enableAddToCart", !instantSearchConfig.enableAddToCart)} />
										</div>
									</Col>
								</Row>
								<hr />
								<h6 className="mb-3 mt-3">Image</h6>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Width</h4></label></Col>
									<Col xs={12} md={6}><Input type="text" className="form-control" value={instantSearchConfig.width ?? ""} onChange={(e) => changeInstantSearchConfig("width", e.target.value)} /></Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Height</h4></label></Col>
									<Col xs={12} md={6}><Input type="text" className="form-control" value={instantSearchConfig.height ?? ""} onChange={(e) => changeInstantSearchConfig("height", e.target.value)} /></Col>
								</Row>
								<Row className="mb-4 align-items-center">
									<Col xs={12} md={4}><label><h4>Type</h4></label></Col>
									<Col xs={12} md={6}>
										<Input type="select" className="form-control" value={instantSearchConfig.imageType ?? ""} onChange={(e) => changeInstantSearchConfig("imageType", e.target.value)}>
											<option value="">Select Image Type</option>
											<option value="product_base_image">Base Image</option>
											<option value="product_small_image">Small Image</option>
											<option value="product_thumbnail_image">Thumbnail</option>
										</Input>
									</Col>
								</Row>
								<Row className="mb-4">
									<Col xs={12} md={4}><label><h4>Sort orders</h4></label></Col>
									<Col xs={12} md={8}>
										<div style={{ display: "flex", flexDirection: "column" }}>
											<p>Enable and configure the different product sorting options. Use drag and drop to customise the order.</p>
											<h4 style={{ marginBottom: "10px" }}>Active</h4>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
												<SortableContext items={instantSortActiveFields} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{instantSortActiveFields.map((id, index) => (
															<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTrigger} onDeactivateBtnClicked={deactiveSortField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
											<h4>Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{instantSortInactiveFields.length > 0 ? (
													<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
														{instantSortInactiveFields.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activeSortField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive sort fields</span>
												)}
											</div>
										</div>
									</Col>
								</Row>
								<Row className="mb-4">
									<Col xs={12} md={4}><label><h4>Facets</h4></label></Col>
									<Col xs={12} md={8}>
										<div style={{ display: "flex", flexDirection: "column" }}>
											<p>Allow customers to explore the catalog by applying multiple filters. Use drag and drop to customise the order.</p>
											<h4 style={{ marginBottom: "10px" }}>Active</h4>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFacetDragEnd}>
												<SortableContext items={instantFacetActiveFields} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{instantFacetActiveFields.map((id, index) => (
															<FacetItem key={index} index={index} id={id} onEditClick={editFacetTrigger} onDeactivateBtnClicked={deactiveFacetField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
											<h4>Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{instantFacetInactiveFields.length > 0 ? (
													<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
														{instantFacetInactiveFields.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activatedFacetField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive facet fields</span>
												)}
											</div>
										</div>
									</Col>
								</Row>
							</>
						)}
						{!isDrupal && (
							<Row className="justify-content-end">
								<Col xs="auto">
									<Button type="button" color="primary" onClick={() => saveInstantSearchConfig()} disabled={saveLoader}>
										{saveLoader ? <Spinner size="sm" /> : "Save"}
									</Button>
								</Col>
							</Row>
						)}
					</form>
				</div>
			</div>

			{renameModalFlag && (
				<Modal isOpen={renameModalFlag} toggle={closeSortTitleModal} centered size="lg">
					<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={closeSortTitleModal}>Rename a sort order</ModalHeader>
					<ModalBody>
						<Form>
							<FormGroup>
								<Label for="sortTitle">Title</Label>
								<Input id="sortTitle" type="text" value={currentSortField.title || ""} onChange={(e) => handleSortTitle(e.target.value)} autoComplete="off" />
							</FormGroup>
							<FormGroup>
								<Label for="sortAttribute">Based on the attribute</Label>
								<Input id="sortAttribute" type="text" value={currentSortField.attribute || ""} disabled />
							</FormGroup>
						</Form>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={closeSortTitleModal}>Cancel</Button>
						<Button color="primary" onClick={saveSortTitle}>Save</Button>
					</ModalFooter>
				</Modal>
			)}

			{editModalFlag && (
				<Modal isOpen={editModalFlag} toggle={closeEditFacetModal} centered>
					<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={closeEditFacetModal}>Edit a facet</ModalHeader>
					<ModalBody>
						<Form>
							<FormGroup>
								<Label for="facetTitle">Title</Label>
								<Input id="facetTitle" type="text" value={currentFacetField.title || ""} onChange={(e) => handleFacetTitle(e.target.value)} autoComplete="off" />
							</FormGroup>
							<FormGroup>
								<Label for="facetType">Facet type</Label>
								<Input type="select" id="facetType" value={currentFacetField.facetType || ""} onChange={(e) => facetTypeChange(e.target.value)}>
									{currentFacetField.attribute === "price"
										? priceOptions.map((opt, i) => <option key={i} value={opt.value}>{opt.label}</option>)
										: otherOptions.map((opt, i) => <option key={i} value={opt.value}>{opt.label}</option>)}
								</Input>
							</FormGroup>
							<FormGroup check inline className="mb-4">
								<Input type="checkbox" checked={!!currentFacetField.searchable} onChange={(e) => searchableChange(e.target.checked)} />
								<Label check>Searchable facet</Label>
							</FormGroup>
							{currentFacetField.attribute && (
								<FormGroup>
									<div className="alert alert-info">This facet has been generated from the default attribute: <strong>{currentFacetField.attribute}</strong></div>
								</FormGroup>
							)}
						</Form>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={closeEditFacetModal}>Cancel</Button>
						<Button color="primary" onClick={saveFacetValue}>Save</Button>
					</ModalFooter>
				</Modal>
			)}

			{editProductGridModal && (isShopify || isBigcommerce) && (
				<Modal isOpen={editProductGridModal} toggle={() => setEditProductGridModal(false)} centered size="lg">
					<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={() => setEditProductGridModal(false)}>Edit JavaScript</ModalHeader>
					<ModalBody>
						<Suspense fallback={<div>Loading...</div>}>
							<JavaScriptCodeEditor value={productGridCode} onChange={(value) => setProductGridCode(value)} />
						</Suspense>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" outline onClick={() => setEditProductGridModal(false)}>Close</Button>
						<Button color="primary" outline onClick={() => setResetProductGridModal(true)}>Reset To Default</Button>
						<Button color="primary" onClick={saveProductGridFromModal} disabled={saveProductGridLoader}>
							{saveProductGridLoader ? <Spinner size="sm" /> : "Save"}
						</Button>
					</ModalFooter>
				</Modal>
			)}
			{resetProductGridModal && (
				<Modal isOpen={resetProductGridModal} toggle={() => setResetProductGridModal(false)} centered>
					<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={() => setResetProductGridModal(false)}>Reset code to default</ModalHeader>
					<ModalBody>Are you sure you want to reset code to default?</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={() => setResetProductGridModal(false)}>Cancel</Button>
						<Button color="primary" onClick={() => { setProductGridCode(conversionBoxInstantSearchScript); setResetProductGridModal(false); }}>Yes, Apply Default Code</Button>
					</ModalFooter>
				</Modal>
			)}

			{editQuickPreviewModal && isBigcommerce && (
				<Modal isOpen={editQuickPreviewModal} toggle={() => setEditQuickPreviewModal(false)} centered size="lg">
					<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={() => setEditQuickPreviewModal(false)}>Edit Quick Preview</ModalHeader>
					<ModalBody>
						<Suspense fallback={<div>Loading...</div>}>
							<JavaScriptCodeEditor value={quickPreviewCode} onChange={(value) => setQuickPreviewCode(value)} />
						</Suspense>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" outline onClick={() => setEditQuickPreviewModal(false)}>Close</Button>
						<Button color="primary" outline onClick={() => setResetQuickPreviewModal(true)}>Reset To Default</Button>
						<Button color="primary" onClick={saveQuickPreviewFromModal} disabled={saveQuickPreviewLoader}>
							{saveQuickPreviewLoader ? <Spinner size="sm" /> : "Save"}
						</Button>
					</ModalFooter>
				</Modal>
			)}
			{resetQuickPreviewModal && (
				<Modal isOpen={resetQuickPreviewModal} toggle={() => setResetQuickPreviewModal(false)} centered>
					<ModalHeader style={{ backgroundColor: "transparent", color: "#333" }} toggle={() => setResetQuickPreviewModal(false)}>Reset code to default</ModalHeader>
					<ModalBody>Are you sure you want to reset code to default?</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={() => setResetQuickPreviewModal(false)}>Cancel</Button>
						<Button color="primary" onClick={() => { setQuickPreviewCode(conversionBoxQuickPreviewScript); setResetQuickPreviewModal(false); }}>Yes, Apply Default Code</Button>
					</ModalFooter>
				</Modal>
			)}
		</>
	);
};

export default InstantSearch;
